import React, { useCallback, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Lock } from 'lucide-react';
import { clsx } from 'clsx';
import {
    SIDEBAR_GROUPS,
    SIDEBAR_STANDALONE_ITEMS,
    ALL_SIDEBAR_ITEMS,
    isNavItemActive,
    isNavItemLocked,
    type SidebarNavItem,
} from '../config/sidebarGroups';
import { useSidebarGroupState } from '../hooks/useSidebarGroupState';
import { LOCKED_FEATURE_TITLE, isFeatureAllowed, useFeatureState, type FeatureKey } from '../auth/features';
import { LockedFeatureDialog } from './LockedFeatureDialog';

interface SidebarNavContentProps {
    isCollapsed?: boolean;
    onNavigate?: () => void;
}

function navTourHook(path: string): string | undefined {
    if (path === '/cases') return 'nav-cases';
    if (path === '/documentation') return 'nav-documentation';
    return undefined;
}

function NavLink({
    item,
    isCollapsed,
    onNavigate,
}: {
    item: SidebarNavItem;
    isCollapsed?: boolean;
    onNavigate?: () => void;
}) {
    const location = useLocation();
    const isActive = isNavItemActive(location.pathname, item.path);

    return (
        <Link
            to={item.path}
            onClick={onNavigate}
            title={isCollapsed ? item.label : undefined}
            data-tour={navTourHook(item.path)}
            className={clsx(
                'flex items-center rounded-md text-sm font-medium transition-colors relative group min-h-[44px]',
                isCollapsed ? 'justify-center p-2.5' : 'gap-3 px-3 py-2',
                isActive
                    ? 'bg-brand-cream/40 dark:bg-primary/10 text-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground'
            )}
        >
            {isActive && (
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-primary rounded-r-full" />
            )}
            <item.icon className={clsx('shrink-0', isCollapsed ? 'w-5 h-5' : 'w-4 h-4')} />
            {!isCollapsed && item.label}

            {isCollapsed && (
                <div className="absolute left-full ml-2 px-2 py-1 bg-popover text-popover-foreground text-xs font-medium rounded-md shadow-md border border-border opacity-0 group-hover:opacity-100 transition-opacity duration-0 pointer-events-none whitespace-nowrap z-50">
                    {item.label}
                </div>
            )}
        </Link>
    );
}

/**
 * A nav item the user's tier does not include: same label and icon, dimmed,
 * with a lock. A button (not a link) so it never navigates; it stays
 * focusable so screen readers announce it, and opens LockedFeatureDialog.
 */
function LockedNavItem({ item, isCollapsed }: { item: SidebarNavItem; isCollapsed?: boolean }) {
    const [open, setOpen] = useState(false);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const close = useCallback(() => {
        setOpen(false);
        triggerRef.current?.focus();
    }, []);

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                aria-disabled="true"
                aria-haspopup="dialog"
                aria-label={`${item.label} (locked: not included in your trial)`}
                title={LOCKED_FEATURE_TITLE}
                data-locked-feature={item.feature}
                data-tour={navTourHook(item.path)}
                onClick={() => setOpen(true)}
                className={clsx(
                    'w-full flex items-center rounded-md text-sm font-medium relative group min-h-[44px] cursor-default',
                    'text-muted-foreground hover:bg-accent/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary',
                    isCollapsed ? 'justify-center p-2.5' : 'gap-3 px-3 py-2 text-left',
                )}
            >
                <span className="relative shrink-0 opacity-[0.45]">
                    <item.icon className={isCollapsed ? 'w-5 h-5' : 'w-4 h-4'} aria-hidden="true" />
                    {isCollapsed && (
                        <Lock className="absolute -right-1.5 -bottom-1.5 w-3 h-3" aria-hidden="true" data-testid="nav-lock" />
                    )}
                </span>
                {!isCollapsed && (
                    <>
                        <span className="flex-1 opacity-[0.45]">{item.label}</span>
                        <Lock className="w-3.5 h-3.5 shrink-0 opacity-60" aria-hidden="true" data-testid="nav-lock" />
                    </>
                )}
                {isCollapsed && (
                    <div className="absolute left-full ml-2 px-2 py-1 bg-popover text-popover-foreground text-xs font-medium rounded-md shadow-md border border-border opacity-0 group-hover:opacity-100 transition-opacity duration-0 pointer-events-none whitespace-nowrap z-50">
                        {item.label} · locked
                    </div>
                )}
            </button>
            {open && <LockedFeatureDialog name={item.label} feature={item.feature} onClose={close} />}
        </>
    );
}

export const SidebarNavContent: React.FC<SidebarNavContentProps> = ({
    isCollapsed = false,
    onNavigate,
}) => {
    const location = useLocation();
    const { expandedGroups, toggleGroup, groupSummaries } = useSidebarGroupState(location.pathname);
    // Items the caller's tier cannot use render locked, not hidden (owner
    // decision 4 Oct 2026). The mobile drawer renders this component too.
    const features = useFeatureState(s => s.features);
    const role = useFeatureState(s => s.role);
    const allowed = (key: FeatureKey) => isFeatureAllowed(features, role, key);
    const renderItem = (item: SidebarNavItem, collapsed?: boolean) => (
        isNavItemLocked(item, allowed)
            ? <LockedNavItem key={item.path} item={item} isCollapsed={collapsed} />
            : <NavLink key={item.path} item={item} isCollapsed={collapsed} onNavigate={onNavigate} />
    );

    if (isCollapsed) {
        return (
            <nav className="space-y-1">
                {ALL_SIDEBAR_ITEMS.map(item => renderItem(item, true))}
            </nav>
        );
    }

    return (
        <nav className="space-y-4">
            {SIDEBAR_GROUPS.map(group => {
                const summary = groupSummaries.find(g => g.id === group.id);
                const isExpanded = summary?.isExpanded ?? group.defaultExpanded;
                const items = group.items;

                return (
                    <div key={group.id}>
                        <button
                            type="button"
                            onClick={() => toggleGroup(group.id)}
                            aria-expanded={isExpanded}
                            className="w-full flex items-center justify-between px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-colors min-h-[44px]"
                        >
                            <span>
                                {group.label}
                                {!isExpanded && (
                                    <span className="ml-1.5 normal-case font-normal text-muted-foreground/80">
                                        · {items.length}
                                    </span>
                                )}
                            </span>
                            <ChevronDown
                                className={clsx(
                                    'w-3.5 h-3.5 transition-transform duration-200',
                                    isExpanded ? 'rotate-0' : '-rotate-90'
                                )}
                            />
                        </button>
                        {isExpanded && (
                            <div className="space-y-1 mt-0.5">
                                {items.map(item => renderItem(item))}
                            </div>
                        )}
                    </div>
                );
            })}
            <div className="mt-3 pt-3 border-t border-border/50 space-y-1">
                {SIDEBAR_STANDALONE_ITEMS.map(item => renderItem(item))}
            </div>
        </nav>
    );
};
